/**
 * S12 设置与可访问性 —— 实现 design/gdd/systems/S12-settings-accessibility.md。
 *
 * SettingsStore 单例：持有"合规与配置"，消费 S9 存档的 settings 子契约；
 * 统辖减少动效双驱动源（user + auto）的权限不对称、色盲模式、系统文本缩放派生值。
 * 不含：动效实现（→ S7 / 渲染层）、存档文件实现（→ S9）、视觉规格（→ 美术圣经）。
 */

import { SaveStore, type ColorblindMode, type SettingsV1 } from './save';

type SettingsListener = (e: { key: string; value: unknown; driver: 'user' | 'auto' | 'default' }) => void;

export type { ColorblindMode };

export interface SettingsResult {
  ok: boolean;
  reason?: 'locked' | 'read-only' | 'reserved' | 'invalid';
}

const CB_VALUES: ColorblindMode[] = ['off', 'deuteranopia', 'protanopia', 'tritanopia'];

export class SettingsStore {
  private static _inst: SettingsStore | null = null;
  static get instance(): SettingsStore {
    if (!SettingsStore._inst) SettingsStore._inst = new SettingsStore();
    return SettingsStore._inst;
  }

  private save = SaveStore.instance;
  /** autoActive 由 PerfDetector 广播（运行时派生，不存档）。MVP+ 默认 false；面板 toggle 受限时即视为锁定。 */
  private autoActive = false;
  private listeners = new Set<SettingsListener>();

  // ── 查询 API（S12 §6.1） ──

  /** 读原始值（userPref 级） */
  get<K extends keyof SettingsV1>(key: K): SettingsV1[K] {
    return this.save.getSettings()[key];
  }

  /** 读解析值（含 auto）：减少动效权限不对称的状态机（§2.2） */
  getResolved(key: 'reducedMotion'): boolean {
    if (key !== 'reducedMotion') return false;
    return this.autoActive ? true : this.save.getSettings().reducedMotion;
  }

  /** = autoActive（派生） */
  isLocked(key: 'reducedMotion'): boolean {
    if (key !== 'reducedMotion') return false;
    return this.autoActive;
  }

  /** 系统文本缩放倍率（启动现读，运行时派生，不入档，SET-04） */
  getSystemTextScale(): number {
    try {
      const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const ratio = fs / 16;
      // 钳制到 ~87.5%–137.5%，覆盖系统 100%–130% 区域（S12 §附-A3）
      return Math.min(1.375, Math.max(0.875, ratio));
    } catch {
      return 1;
    }
  }

  /** 性能档位（MVP+ 恒为 'auto'，只读显示，SET-02） */
  getPerfTier(): 'auto' {
    return 'auto';
  }

  // ── 变更 API（§6.1） ──

  set(key: 'reducedMotion' | 'colorblindMode', value: boolean | ColorblindMode): SettingsResult {
    if (key === 'reducedMotion') {
      if (this.autoActive) return { ok: false, reason: 'locked' }; // E3
      this.save.updateSettings({ reducedMotion: !!value });
    } else if (key === 'colorblindMode') {
      const v = value as ColorblindMode;
      if (!CB_VALUES.includes(v)) return { ok: false, reason: 'invalid' };
      this.save.updateSettings({ colorblindMode: v });
    } else {
      return { ok: false, reason: 'read-only' }; // perfTier / systemTextScale 只读；audio 预留
    }
    this.emit(key, value, 'user');
    return { ok: true };
  }

  /** 恢复默认（面板按钮）：清 userPref + 色盲；档位 / 系统缩放不变 */
  resetAll(): void {
    this.save.updateSettings({ reducedMotion: false, colorblindMode: 'off' });
    this.emit('reducedMotion', false, 'user');
    this.emit('colorblindMode', 'off', 'user');
  }

  /** PerfDetector 广播（§2.2 / §2.3）：autoActive 翻转 */
  setAutoActive(v: boolean): void {
    if (v === this.autoActive) return;
    this.autoActive = v;
    this.emit('reducedMotion', this.getResolved('reducedMotion'), 'auto');
  }

  subscribe(l: SettingsListener): () => void {
    this.listeners.add(l);
    return () => { this.listeners.delete(l); };
  }

  private emit(key: string, value: unknown, driver: 'user' | 'auto' | 'default'): void {
    for (const l of [...this.listeners]) {
      try { l({ key, value, driver }); } catch (e) { console.error('[settings] listener error', e); }
    }
  }
}
